import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Star, Briefcase, GraduationCap, TrendingUp, Home, DollarSign } from "lucide-react";

function MyNewNormals() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const empowermentAreas = [
    {
      icon: GraduationCap,
      title: "Skills Development",
      description: "Comprehensive training programs that build marketable skills for sustainable income generation",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Briefcase,
      title: "Business Training",
      description: "Entrepreneurship education and small business development support for single mothers",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: TrendingUp,
      title: "Income Enhancement",
      description: "Strategic interventions to increase earning potential and create financial stability",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Home,
      title: "Poverty Elimination",
      description: "Holistic approach to breaking cycles of poverty in single-mother households",
      color: "from-orange-500 to-red-500"
    }
  ];

  const outcomes = [
    { icon: DollarSign, metric: "↑150%", label: "Income Increase", description: "Average earnings improvement" },
    { icon: Star, metric: "85%", label: "Skills Mastery", description: "Successful program completion" },
    { icon: Home, metric: "75%", label: "Stability Achieved", description: "Sustainable household income" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      <Header />
      
      {/* Hero Image Section */}
      <div className="relative pt-24 sm:pt-28">
        <img 
          src="img/woman.jpg" 
          className="w-full h-64 md:h-96 object-cover"
          alt="Empowered Single Mother"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pt-20 sm:pt-24">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Heart className="w-4 h-4 mr-2" />
              Women's Empowerment Initiative
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent">
                My New Normals
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Transforming lives through skills, opportunity, and empowerment
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="mission-content"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['mission-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8">
              Breaking the <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Poverty Cycle</span>
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-purple-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">
                  <strong className="text-purple-700">My New Normals</strong> is a transformative initiative 
                  dedicated to eliminating poverty among single-mother households through 
                  comprehensive skill advancement and income enhancement programs.
                </p>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-r-2xl">
                  <p className="text-lg text-gray-700 font-medium">
                    We believe every single mother deserves the opportunity to build a sustainable, 
                    dignified future for herself and her children through skills development and economic empowerment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Empowerment Areas Grid */}
          <div 
            id="empowerment-grid"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-300 ${
              isVisible['empowerment-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Approach</span>
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {empowermentAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${area.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4 text-center">{area.title}</h4>
                    <p className="text-gray-600 text-center leading-relaxed">{area.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Impact Metrics */}
          <div 
            id="impact-metrics"
            data-animate
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible['impact-metrics'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Measurable <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Impact</span>
              </h3>
              <p className="text-xl text-gray-600">Transforming lives through proven results</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {outcomes.map((outcome, index) => {
                const Icon = outcome.icon;
                return (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 text-white text-center transform hover:scale-105 transition-all duration-300"
                  >
                    <Icon className="w-12 h-12 mx-auto mb-4" />
                    <div className="text-4xl font-black mb-2">{outcome.metric}</div>
                    <div className="text-xl font-semibold mb-2">{outcome.label}</div>
                    <div className="text-purple-100">{outcome.description}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      <div className="py-16 bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div 
            id="testimonial"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['testimonial'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-8 md:p-12">
              <div className="text-6xl text-purple-600 mb-6">"</div>
              <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed mb-6">
                My New Normals didn't just teach me skills—it gave me hope, confidence, 
                and the tools to create a better future for my children. Today, I run my own business 
                and support my family with dignity.
              </p>
              <div className="flex items-center justify-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-gray-800">Sarah M.</div>
                  <div className="text-gray-600">Program Graduate & Entrepreneur</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Star className="w-5 h-5 mr-3" />
              Empowerment • Independence • Transformation
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Invest in Women's Potential
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Partner with us to transform the lives of single mothers through skills development, 
              business training, and economic empowerment. Together, we can break the cycle of poverty 
              and create sustainable change.
            </p>
            
            <button 
              onClick={() => window.location.href = '/contact-us'}
              className="bg-white text-purple-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Support Women's Empowerment <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default MyNewNormals;